import { Box, Stack, Typography } from "@mui/material";

import { Card, Skeleton } from "@repo/ui";
import { useCoverImageUrl } from "hooks/api/useArticleCovers";
import { articleContentApi, type WebsiteArticle } from "services/coversApi";

/**
 * VRG-733 — the cover as a reader meets it, on the article's Overview (Spencer, 2026-10-29: "the
 * overview page of the articles should also show the graphics for the article"). A perspective's
 * body is words only (headings, paragraphs, lists); its one graphic is the cover, and the website
 * uses it in three frames, each cropping differently:
 *
 *   Article page    `InsightCover` in app/insights/[slug]/page.tsx: 21:9 from md (16:9 below),
 *                   `object-cover`, centred.
 *   /insights card  components/sections/InsightsGrid.tsx: the cover's own 1200:630, centred.
 *   Shared link     app/insights/[slug]/opengraph-image.tsx → lib/og.tsx `ogInsightPhoto`:
 *                   1200×630, a left-to-right shade (#01010a at 85% → 65% at 24% → clear at 46%),
 *                   the logo, the "INSIGHTS" eyebrow and the title in a 380px column on the left,
 *                   the tagline at the foot. Drawn here with the same geometry, scaled; it is a
 *                   likeness of the website's image, not that image (the route is staged with its
 *                   page and 404s in production until publish).
 *
 * Mirrors those files by hand. If the website changes a frame, this changes with it.
 */
export const CoverPlacements = ({ article }: { article: WebsiteArticle }) => {
  const { src } = useCoverImageUrl(
    article.cover.type === "image"
      ? articleContentApi.coverUrl(article.section, article.slug, 1280)
      : null,
  );
  if (article.cover.type !== "image") return null;
  const alt = article.cover.alt;
  return (
    <Card title="The cover, where readers see it" sx={{ p: 4, mb: 0 }}>
      <Stack spacing={3}>
        <Frame
          label="Article page · 21:9 on desktop, 16:9 on phones"
          ratio="21 / 9"
          src={src}
          alt={alt}
        />
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Frame
              label="/insights card · 1200:630"
              ratio="1200 / 630"
              src={src}
              alt={alt}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SharedLink src={src} title={article.title} />
          </Box>
        </Stack>
      </Stack>
    </Card>
  );
};

const Frame = ({
  label,
  ratio,
  src,
  alt,
}: {
  label: string;
  ratio: string;
  src: string | null;
  alt: string;
}) => (
  <Stack spacing={1}>
    <Box
      sx={{
        width: "100%",
        aspectRatio: ratio,
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "#0e0d12",
      }}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <Skeleton height="100%" />
      )}
    </Box>
    <Typography variant="body4" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);

/** `ogInsightPhoto`'s layout in container units: 1200 px wide = 100cqw, so 1 px = 100/1200 cqw. */
const px = (n: number) => `${(n * 100) / 1200}cqw`;

const SharedLink = ({ src, title }: { src: string | null; title: string }) => (
  <Stack spacing={1}>
    <Box sx={{ containerType: "inline-size", width: "100%" }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "1200 / 630",
          borderRadius: "12px",
          overflow: "hidden",
          backgroundColor: "#01010a",
          fontFamily: "Lato, sans-serif",
        }}
      >
        {src ? (
          <img
            src={src}
            alt=""
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : null}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(to right, #01010ad9 0%, #01010aa6 24%, #01010a00 46%)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            p: `${px(64)} ${px(64)}`,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              fontSize: px(30),
              fontWeight: 700,
              letterSpacing: "0.18em",
              color: "#ffffff",
              mb: px(48),
              lineHeight: 1,
            }}
          >
            VERA
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              maxWidth: px(380),
              display: "flex",
              flexDirection: "column",
              gap: px(16),
            }}
          >
            <Box
              sx={{
                fontSize: px(20),
                fontWeight: 700,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "#b68afe",
              }}
            >
              Insights
            </Box>
            <Box
              sx={{
                fontSize: px(title.length > 56 ? 38 : 42),
                fontWeight: 700,
                lineHeight: 1.12,
                letterSpacing: "-0.02em",
                color: "#ffffff",
              }}
            >
              {title}
            </Box>
          </Box>
          <Box
            sx={{
              fontSize: px(22),
              fontWeight: 700,
              lineHeight: 1.3,
              color: "rgba(255,255,255,0.78)",
              maxWidth: px(380),
            }}
          >
            The product identity infrastructure
          </Box>
        </Box>
      </Box>
    </Box>
    <Typography variant="body4" color="text.secondary">
      Shared link · 1200×630, title over the left (a likeness of the website’s
      own image)
    </Typography>
  </Stack>
);
